// ======================================================================
// File: src/modules/ppe/fms/components/followups/FollowupDrawer.jsx
// Purpose: Drawer for creating followup (ERP pattern)
// Enhanced with Domain → Reference Mapping + Recurrence Support
// ======================================================================

import React, { useEffect, useState } from "react";
import fmsService from "../../services/fmsService";
import axiosClient from "../../../../../api/axiosClient";

export default function FollowupDrawer({ open, onClose, onSaved }) {

  const initialForm = {
    title: "",
    description: "",
    priority: "MEDIUM",
    followup_type: "MANUAL",
    due_date: "",
    domain_code: "",
    purpose_code: "",
    assigned_to: "",
    source_module: "",
    source_reference: "",
    is_recurring: false,
    recurrence_days: ""
  };

  const [form, setForm] = useState(initialForm);

  const [domains, setDomains] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [referenceTypes, setReferenceTypes] = useState([]);
  const [referenceOptions, setReferenceOptions] = useState([]);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadLookups();
    }
  }, [open]);

  // ===============================
  // LOAD LOOKUPS
  // ===============================

  const loadLookups = async () => {
    try {
      const dRes = await axiosClient.get("/api/ppe/fms/lookups/domains");
      setDomains(dRes.data.data || []);

      const pRes = await axiosClient.get("/api/ppe/fms/lookups/purposes");
      setPurposes(pRes.data.data || []);

      const eRes = await axiosClient.get("/employees/");
      setEmployees(eRes.data?.data || []);

    } catch (err) {
      console.error("Failed to load lookups", err);
    }
  };

  // ===============================
  // LOAD REFERENCE TYPES WHEN DOMAIN CHANGES
  // ===============================

  useEffect(() => {
    if (!form.domain_code) {
      setReferenceTypes([]);
      setReferenceOptions([]);
      setForm(prev => ({
        ...prev,
        source_module: "",
        source_reference: ""
      }));
      return;
    }

    const loadReferenceTypes = async () => {
      try {
        const res = await axiosClient.get("/api/ppe/fms/reference-types", {
          params: { domain_code: form.domain_code }
        });

        setReferenceTypes(res.data.data || []);
        setReferenceOptions([]);
        setForm(prev => ({
          ...prev,
          source_module: "",
          source_reference: ""
        }));

      } catch (err) {
        console.error("Failed to load reference types", err);
      }
    };

    loadReferenceTypes();

  }, [form.domain_code]);

  // ===============================
  // LOAD REFERENCE OPTIONS WHEN TYPE CHANGES
  // ===============================

  useEffect(() => {
    if (!form.source_module) {
      setReferenceOptions([]);
      setForm(prev => ({
        ...prev,
        source_reference: ""
      }));
      return;
    }

    const loadReferenceOptions = async () => {
      try {
        const res = await axiosClient.get("/api/ppe/fms/reference-options", {
          params: { reference_type: form.source_module }
        });

        setReferenceOptions(res.data.data || []);
        setForm(prev => ({
          ...prev,
          source_reference: ""
        }));

      } catch (err) {
        console.error("Failed to load reference options", err);
      }
    };

    loadReferenceOptions();

  }, [form.source_module]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ===============================
  // VALIDATION
  // ===============================

  const validate = () => {
    if (!form.title) {
      alert("Title is required");
      return false;
    }

    if (!form.domain_code) {
      alert("Please select domain");
      return false;
    }

    if (!form.purpose_code) {
      alert("Please select purpose");
      return false;
    }

    if (!form.due_date) {
      alert("Due date is required");
      return false;
    }

    if (form.is_recurring && !form.recurrence_days) {
      alert("Please enter recurrence days");
      return false;
    }

    return true;
  };

  // ===============================
  // SUBMIT
  // ===============================

  const handleSubmit = async () => {
    try {
      if (!validate()) return;

      setSaving(true);

      await fmsService.createFollowup(form);

      onSaved();
      onClose();
      setForm(initialForm);

    } catch (err) {
      alert("Failed to save followup");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="erp-drawer-overlay">
      <div className="erp-drawer">

        <div className="erp-drawer-header">
          <h5>Create Followup</h5>
          <button onClick={onClose}>X</button>
        </div>

        <div className="erp-drawer-body">

          {/* TITLE */}
          <div className="mb-3">
            <label>Title *</label>
            <input
              className="form-control"
              name="title"
              value={form.title}
              onChange={handleChange}
            />
          </div>

          {/* DESCRIPTION */}
          <div className="mb-3">
            <label>Description</label>
            <textarea
              className="form-control"
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </div>

          {/* DOMAIN + PURPOSE */}
          <div className="row mb-3">

            <div className="col-md-6">
              <label>Domain *</label>
              <select
                className="form-control"
                name="domain_code"
                value={form.domain_code}
                onChange={handleChange}
              >
                <option value="">Select Domain</option>
                {domains.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label>Purpose *</label>
              <select
                className="form-control"
                name="purpose_code"
                value={form.purpose_code}
                onChange={handleChange}
              >
                <option value="">Select Purpose</option>
                {purposes.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* REFERENCE TYPE */}
          {referenceTypes.length > 0 && (
            <div className="mb-3">
              <label>Reference Type (Optional)</label>
              <select
                className="form-control"
                name="source_module"
                value={form.source_module}
                onChange={handleChange}
              >
                <option value="">Select Reference Type</option>
                {referenceTypes.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* REFERENCE NUMBER */}
          {referenceOptions.length > 0 && (
            <div className="mb-3">
              <label>Reference No (Optional)</label>
              <select
                className="form-control"
                name="source_reference"
                value={form.source_reference}
                onChange={handleChange}
              >
                <option value="">Select Reference</option>
                {referenceOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ASSIGN TO */}
          <div className="mb-3">
            <label>Assign To</label>
            <select
              className="form-control"
              name="assigned_to"
              value={form.assigned_to}
              onChange={handleChange}
            >
              <option value="">Self (Default)</option>
              {(Array.isArray(employees) ? employees : []).map((e) => (
                <option key={e.id} value={e.id}>
                  {e.username} ({e.email})
                </option>
              ))}
            </select>
          </div>

          {/* PRIORITY + DUE DATE */}
          <div className="row">
            <div className="col-md-6">
              <label>Priority</label>
              <select
                className="form-control"
                name="priority"
                value={form.priority}
                onChange={handleChange}
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            <div className="col-md-6">
              <label>Due Date *</label>
              <input
                type="date"
                className="form-control"
                name="due_date"
                value={form.due_date}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* RECURRENCE */}
          <div className="mt-3 border p-2 rounded">
            <label>
              <input
                type="checkbox"
                checked={form.is_recurring}
                onChange={(e) =>
                  setForm({ ...form, is_recurring: e.target.checked })
                }
              />
              &nbsp;Create recurring followup on completion
            </label>

            {form.is_recurring && (
              <div className="mt-2">
                <label>Next after (days)</label>
                <input
                  type="number"
                  className="form-control"
                  name="recurrence_days"
                  value={form.recurrence_days}
                  onChange={handleChange}
                  min="1"
                />
              </div>
            )}
          </div>

        </div>

        <div className="erp-drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Followup"}
          </button>
        </div>

      </div>
    </div>
  );
}
